import { EFFECTS, NAVIGATION_CLASS, NavigationConstants, PRODUCTS_HUB_TRIAL_TYPE_SLUGS } from './navigation.constants';
import {
  AlertsNavigationParams,
  RiskManagementNavigationParams,
  DrillDownToBlockList,
  EASMAssetsInventoryNavigationParams,
  IncidentsNavigationParams,
  ModuleStatusParams,
  MonthlyUsageReportParams,
  NavigationItem,
  NetworkNavigationParams,
  TaskTarget,
  InventoryFilter,
} from './navigation.interfaces';
import { Target } from '@nirvana/shared/dtos/types/dashboards/common';
import {
  ExtConstants,
  ExtDependencies,
  ModuleExtDependencies,
  NetworkConstants,
  ReportTarget,
  StandardNetworkNavigationData,
} from './navigation.types';
import { TIME_RANGE } from '@nirvana/shared/constants';
import moment from 'moment-timezone';
import { TimeRange, TimeRangeKind } from '@nirvana/shared/dtos/types/shared/time';
import { buildHash } from './navigation.utils';
import { InProductTrialTypeEnum } from '@nirvana/shared/dtos/types/in-product-trials';

export class NavigationHelper {
  /**
   * Navigate to a specific path using browser navigation
   *
   * @param {string} path
   * @param {?Record<string, string>} query
   * @param {?boolean} newTab
   */
  public static navigateTo(path: string, query?: Record<string, any>, newTab?: boolean): void {
    const hash = buildHash(path, query);

    if (newTab) {
      const [baseURL] = window.location.href.split('#');
      window.open(baseURL + hash, '_blank')?.focus();
    } else {
      window.location.hash = hash;
    }
  }

  public static shouldAddHandlers(extDependencies: ExtDependencies): boolean {
    if (!extDependencies) {
      console.warn('Drill down functionality is disabled');
      return false;
    }
    return true;
  }

  /**
   *
   * @param extDependencies
   * @param navigationItems
   * @param section If the widget can only drill down to a single console page/module, this param needs to be sent
   * If the widget can drill down to different pages, the param is not sent because it should already be set on the navigationItem
   */
  public static removeUnnavigableItems<NavigationParams>(
    extDependencies: ExtDependencies,
    navigationItems: Array<NavigationItem<NavigationParams>>,
    section?: string
  ): Array<NavigationItem<NavigationParams>> {
    if (section) {
      if (!NavigationHelper.hasModule(extDependencies, section)) {
        return [];
      }

      return navigationItems;
    }

    return navigationItems.filter(navigationItem => {
      if (navigationItem.section) {
        return NavigationHelper.hasModule(extDependencies, navigationItem.section);
      }
      throw new Error('Function was called with wrong parameters|');
    });
  }

  public static navigateToNetwork(
    extDependencies: ExtDependencies,
    params: NetworkNavigationParams,
    serviceId = null
  ): void {
    let path = '/network';

    if (!extDependencies.isCloud()) {
      const servicePath =
        '/' +
        this.getServiceNameById(extDependencies, serviceId ?? extDependencies.Constants.ServiceTypes.EPS).toLowerCase();
      path += servicePath;
    }

    this.navigateTo(path, params);
  }

  public static navigateToRiskManagement(params: RiskManagementNavigationParams): void {
    // Navigate to a subsection containing otherwise to the risk management dashboard
    if (params.section) {
      const filters: Array<InventoryFilter<string>> = params.filters ?? [];

      filters.push({
        companyId: {
          exactMatch: params.company.id,
        },
      });

      this.navigateTo('/risk-management-' + params.section, { filters }, params.newTab ?? false);
    } else {
      this.navigateTo(
        '/risk-management',
        {
          companyId: params.company.id,
        },
        params.newTab ?? false
      );
    }
  }

  public static openEndpointDetailsModal(
    extDependencies: ExtDependencies,
    protectedEntityId: string,
    tabId?: string
  ): void {
    const epsServiceId = NavigationHelper.getEPSServiceId(extDependencies);
    const detailsController = extDependencies.detailsController;

    if (tabId) {
      detailsController.setNextSelectedTab(tabId);
    }
    detailsController.showDetails(protectedEntityId, epsServiceId);
  }

  public static navigateToIncidents(extDependencies: ExtDependencies, params: IncidentsNavigationParams) {
    const options =
      Array.isArray(params) && params.length
        ? {
            filters: [...params],
          }
        : undefined;

    this.navigateTo('/incidents', options);
  }

  public static getStandardNetworkNavigationData(targets: Array<Target>): StandardNetworkNavigationData {
    const standardAdditionalFilters = {
      ...NavigationConstants.NETWORK.ADDITIONAL_FILTERS.TYPE.ALL_MACHINES,
      ...NavigationConstants.NETWORK.ADDITIONAL_FILTERS.SECURITY_MANAGEMENT,
      ...NavigationConstants.NETWORK.ADDITIONAL_FILTERS.DEPTH.RECURSIVE,
    };

    const commonGridFilters = [
      {
        property: NavigationConstants.NETWORK.FILTERS.PROPERTIES.COMPANY.FILTER,
        value: targets.map(target => ({ id: target.companyId, name: target.companyName })),
      },
    ];

    return { standardAdditionalFilters, commonGridFilters };
  }

  public static navigateToAlerts(extDependencies: ExtDependencies, params: AlertsNavigationParams): void {
    this.navigateTo('/threats-explorer', params);
  }

  public static navigateToEndpointModulesStatus(extDependencies: ExtDependencies, params: ModuleStatusParams): void {
    const module = NavigationHelper.getModuleByName(extDependencies, NavigationConstants.REPORTS.NAME);

    const epsServiceId = NavigationHelper.getEPSServiceId(extDependencies);

    const report = {
      name: params.title,
      type: extDependencies.Constants.Reports.EPS.REPORT_TYPE_MODULE_STATUS,
      recordSource: epsServiceId,
      targets: [
        {
          company: {
            id: params.targetData.companyId,
          },
          protectedEntityId: params.targetData.networkNodeId,
          type: 4,
        },
      ],
      scheduleInfo: {
        type: extDependencies.Constants.Reports.SCHEDULE_TYPE_NOW,
        recurrence: extDependencies.Constants.Reports.SCHEDULE_RECURRENCE_NOW,
      },
    };

    module.generateReportFromOtherModule(epsServiceId, report);
  }

  public static navigateToMonthlyUsageReport(extDependencies: ExtDependencies, params: MonthlyUsageReportParams): void {
    const module = NavigationHelper.getModuleByName(extDependencies, NavigationConstants.REPORTS.NAME);

    const epsServiceId = NavigationHelper.getEPSServiceId(extDependencies);

    const report = {
      name: params.title,
      type: extDependencies.Constants.Reports.EPS.REPORT_TYPE_MODULE_STATUS,
      recordSource: extDependencies.Constants.ServiceTypes.EPS,
      options: {
        reportingInterval: extDependencies.Constants.Reports.REPORTING_INTERVAL,
      },
      targets: params.targets,
      scheduleInfo: {
        type: extDependencies.Constants.Reports.SCHEDULE_TYPE_NOW,
        recurrence: extDependencies.Constants.Reports.SCHEDULE_RECURRENCE_NOW,
      },
    };

    module.generateReportFromOtherModule(epsServiceId, report);
  }

  public static async navigateToEASMAssetsInventory(params: EASMAssetsInventoryNavigationParams): Promise<void> {
    this.navigateTo('/easm-assets-inventory', params);
  }

  public static async navigateToEASMArtifactsInventory(params: EASMAssetsInventoryNavigationParams): Promise<void> {
    this.navigateTo('/easm-artifacts-inventory', params);
  }

  public static showReportForm(extDependencies: ExtDependencies, timeRange: TimeRange, selectedTarget: Target) {
    let lastDaysNum;
    if (timeRange.kind === TimeRangeKind.RELATIVE_TIME_RANGE) {
      lastDaysNum = timeRange.relative?.lastNumDays;
    }

    if (!lastDaysNum) {
      throw Error('Unable to set the reporting interval for the reports form');
    }

    const reportingInterval = NavigationConstants.REPORTS.TIME_RANGE_MAP[lastDaysNum];

    const module = NavigationHelper.getModuleByName(extDependencies, NavigationConstants.REPORTS.NAME);
    const epsServiceId = NavigationHelper.getEPSServiceId(extDependencies);

    module.showExecutiveSummaryRecordForm(epsServiceId, reportingInterval, selectedTarget);
  }

  public static showReportFromNetwork(extDependencies, targets: Array<ReportTarget>) {
    const module = NavigationHelper.getModuleByName(extDependencies, NavigationConstants.REPORTS.NAME);
    const epsServiceId = NavigationHelper.getEPSServiceId(extDependencies);
    module.quickReportFromNetwork(epsServiceId, {
      // This is going to need to be implemented when adding data providers for SVE/MDM
      // viewTypes: {},
      targets: targets,
      disableTargets: false,
    });
  }

  public static openScanTaskModal(extDependencies: ExtDependencies, endpoint: TaskTarget) {
    const controller = extDependencies.getController(NavigationConstants.NETWORK.CONTROLLERS.SCAN_TASK);
    controller.externalTargets = [endpoint];
    controller.showWindow();
  }

  public static viewPolicyDetails(policyId: string) {
    this.navigateTo('/policies/edit/' + policyId);
  }

  /**
   * This function will open the policy details of the policy with id policyId.
   * After clicking the Cancel button, the user will be redirected to the module
   * given as parameter. Keep in mind that the module should implement a function
   * called showPolicy.
   * @param policyId
   * @param moduleToReturnTo
   */
  public static viewPolicyDetailsFromModule(policyId: string, moduleToReturnTo: string) {
    //@ts-ignore
    const module = app.getModuleByName(moduleToReturnTo);
    module.showPolicy(policyId, module.modulePanel);
  }

  /**
   *
   * @param extDependencies
   * @param endpoint
   * @param isolate True will isolate endpoint, false will revert isolation
   */
  public static isolateEndpointTask(extDependencies: ExtDependencies, endpoint: TaskTarget, isolate: boolean = true) {
    const controller = extDependencies.getController(NavigationConstants.NETWORK.CONTROLLERS.ISOLATE_TASK);
    // snd parameter should be an array as the console function changed
    controller.submitTask(endpoint.specifics, [endpoint], isolate);
  }

  public static updateNavigableItems<NavigationParams>(
    navigationItems: Array<NavigationItem<NavigationParams>>,
    navigationHandlersCache: Array<(event: any) => void>,
    navigationHandler: ((event: any, navigationParams, section?) => void) | undefined
  ): void {
    for (let i = 0; i < navigationItems.length; i++) {
      const navigationItem = navigationItems[i];

      let clickHandler;

      if (navigationHandler) {
        if (navigationItem.section) {
          clickHandler = event => navigationHandler(event, navigationItem.params, navigationItem.section);
        } else {
          clickHandler = event => navigationHandler(event, navigationItem.params);
        }
        navigationItem.elements.forEach(element => {
          if (!element.classList.contains(NAVIGATION_CLASS)) {
            element.classList.add(NAVIGATION_CLASS);
          }
        });
      } else {
        navigationItem.elements.forEach(element => element.classList.remove(NAVIGATION_CLASS));
      }

      if (navigationHandlersCache[i]) {
        navigationItem.elements.forEach(element => element.removeEventListener('click', navigationHandlersCache[i]));
      }

      navigationHandlersCache[i] = clickHandler;
      if (clickHandler) {
        navigationItem.elements.forEach(element => element.addEventListener('click', clickHandler));
      }
    }
  }

  public static addDrillDownEffectsForMultipleElements(sections: HTMLElement[][]) {
    sections.forEach(sectionElements => {
      if (sectionElements.length === 1) {
        return;
      }

      sectionElements.forEach(element => {
        element.addEventListener('mouseover', (c: any) => {
          sectionElements.forEach(unhandledElement => {
            if (!unhandledElement.classList.contains(EFFECTS.HOVER)) {
              unhandledElement.classList.add(EFFECTS.HOVER);
            }
          });
        });

        element.addEventListener('mouseout', (c: any) => {
          sectionElements.forEach(handledElement => {
            handledElement.classList.remove(EFFECTS.HOVER);
            handledElement.classList.remove(EFFECTS.ACTIVE);
          });
        });

        element.addEventListener('focus', () => {
          sectionElements.forEach(unhandledElement => {
            if (!unhandledElement.classList.contains(EFFECTS.FOCUS)) {
              unhandledElement.classList.add(EFFECTS.FOCUS);
            }
          });
        });

        element.addEventListener('blur', () => {
          sectionElements.forEach(handledElement => {
            handledElement.classList.remove(EFFECTS.FOCUS);
          });
        });

        element.addEventListener('mousedown', () => {
          sectionElements.forEach(unhandledElement => {
            if (!unhandledElement.classList.contains(EFFECTS.ACTIVE)) {
              unhandledElement.classList.add(EFFECTS.ACTIVE);
            }
          });
        });

        element.addEventListener('mouseup', () => {
          sectionElements.forEach(handledElement => {
            handledElement.classList.remove(EFFECTS.ACTIVE);
          });
        });
      });
    });
  }

  public static getTimeRangeToLastSyncConstant(extConstants: ExtConstants) {
    return new Map([
      [TIME_RANGE.LAST_24_HOURS, extConstants.Network.lastSyncOptions.LAST_24_HOURS],
      [TIME_RANGE.LAST_7_DAYS, extConstants.Network.lastSyncOptions.LAST_7_DAYS],
      [TIME_RANGE.LAST_30_DAYS, extConstants.Network.lastSyncOptions.LAST_30_DAYS],
    ]);
  }

  public static getLastSyncToTimeRange(extConstants: ExtConstants) {
    const timeRangeToLastSyncConstant = this.getTimeRangeToLastSyncConstant(extConstants);
    const lastSyncToTimeRange = new Map();
    timeRangeToLastSyncConstant.forEach((value, key) => {
      lastSyncToTimeRange.set(value, key);
    });
    return lastSyncToTimeRange;
  }

  public static mapTimeRangeToLastSyncConstants(timeRange: number, extConstants: ExtConstants): number {
    const timeRangeToLastSyncConstant = this.getTimeRangeToLastSyncConstant(extConstants);

    if (!timeRangeToLastSyncConstant.has(timeRange)) {
      //This is a programattic error, it should never happen
      console.error('Unable to select the correct constant for Last Seen when doing data drill-down');
      return extConstants.Network.lastSyncOptions.ALL;
    }

    return timeRangeToLastSyncConstant.get(timeRange) as number;
  }

  public static formatDateForIncidents(timestamp: number, timezone: string): string {
    return moment(timestamp).tz(timezone).format('D MMM YYYY HH:mm');
  }

  public static getModuleByName(extDependencies: ModuleExtDependencies, name: string) {
    return extDependencies.getModuleByName(name);
  }

  private static getEPSServiceId(extDependencies: any) {
    const epsServiceId = extDependencies.Constants.ServiceTypes.EPS;
    const availableServices = extDependencies.getAvailableServices();
    if (!availableServices.some(service => service.id === epsServiceId)) {
      throw new Error('EPS not in the list of available services');
    }

    return epsServiceId;
  }

  private static getServiceNameById(extDependencies: ExtDependencies, serviceId: number): string {
    if (typeof extDependencies.Constants.ServiceNames[serviceId] !== 'string') {
      throw new Error("Can't determine service name for service id:" + serviceId);
    }
    return extDependencies.Constants.ServiceNames[serviceId];
  }

  private static hasModule(extDependencies: ExtDependencies, section: string): boolean {
    const module = NavigationHelper.getModuleByName(extDependencies, section);
    return !!module;
  }

  public static showAndFillBlockListForm(extDependencies: ExtDependencies, data: DrillDownToBlockList) {
    const addHashWindow = extDependencies.showHashBlockListForm();
    const form = addHashWindow.down(NavigationConstants.BLOCK_LIST.FORM);

    // radiogroup does not seem to work with form.getForm().setValues({...});
    form.down('radiogroup[name=type]').setValue({ type: NavigationConstants.BLOCK_LIST.HASH_TYPE.SHA256 });

    // set the rest of the values
    form.getForm().setValues({
      note: data.blockListNote,
      hashes: [data.hash],
    });
  }

  public static showWhatsNew() {
    //@ts-ignore
    const controller = app.getController(NavigationConstants.WHATS_NEW.CONTROLLER);
    controller.showWhatsNewPanel();
  }

  public static drillDownToMyCompany(tabId) {
    switch (tabId) {
      case NavigationConstants.MY_COMPANY.TABS.LICENSING:
        this.navigateTo('/my-company/licensing');
        break;
      case NavigationConstants.MY_COMPANY.TABS.AUTHENTICATION:
        this.navigateTo('/my-company/authentication');
        break;
      case NavigationConstants.MY_COMPANY.TABS.EARLY_ACCESS:
        this.navigateTo('/my-company/early-access');
        break;
      default:
        this.navigateTo('/my-company');
    }
  }

  public static drillDownToEditCompany(companyId, tabId) {
    //@ts-ignore
    const controller = app.getController(NavigationConstants.COMPANIES.CONTROLLER);
    controller.showEditCompany(companyId, null, tabId);
  }

  /**
   * Redirect to the Network section and after that, open the detail panel of the
   * endpoint with id: entityId. After clicking the Back button of the panel the user
   * will arrive in the Network section.
   *
   * @param entityId - protectedEntity _id of the endpoint.
   */
  public static navigateToNetworkAndShowDetailsPanel(entityId: string) {
    this.navigateTo('/network/endpoint/' + entityId);
  }

  /**
   *
   * @param networkV2NavigationParams see apps/webcomponents/src/networkV2/networkAppContainer/helpers/types.ts#ExternalParams
   */
  public static navigateToNetworkV2(networkV2NavigationParams?: any): void {
    if (networkV2NavigationParams) {
      // @ts-ignore
      Ext.state.Manager.set('network-v2', { savedViewConfiguration: networkV2NavigationParams });
    }

    this.navigateTo('/network');
  }

  public static navigateToCompliance(): void {
    this.navigateTo('/risk-management-compliance');
  }

  public static navigateToCompanies() {
    this.navigateTo('/companies');
  }

  /**
   * Navigate to the Products Hub section
   *
   * @param {InProductTrialTypeEnum} trialType - The type of trial to navigate to
   * @param {string} icidParam - Used for adobe analytics tracking, to add context about where the user came from
   */
  public static navigateToProductsHub(trialType?: InProductTrialTypeEnum, icidParam?: string) {
    let path = '/in-product-trials/';
    const queryObject = {};

    if (trialType !== undefined) {
      path += PRODUCTS_HUB_TRIAL_TYPE_SLUGS[trialType];
    }

    if (icidParam) {
      queryObject['icid'] = icidParam;
    }

    this.navigateTo(path, queryObject);
  }
}
